/**
 * Numeric channel number for the restricted (TVSimpleCard) layout. The API
 * field is a number; the programme-card prop also accepts a string, so this
 * drops empty and non-numeric values instead of passing `NaN`.
 */
export const toChannelNumber = (
  channelNumber?: number | string
): number | undefined => {
  if (channelNumber == null || channelNumber === '') return undefined
  const n = Number(channelNumber)
  return Number.isFinite(n) ? n : undefined
}

/**
 * Channel label for a programme card: "119. Boomerang tv" (UI-5.4).
 *
 * A pinlocked channel shows the number only — its name is hidden.
 */
export const formatChannelLabel = ({
  channelNumber,
  channelName,
  locked = false,
}: {
  channelNumber?: number | string
  channelName?: string
  locked?: boolean
}): string => {
  const number =
    channelNumber == null || channelNumber === '' ? '' : String(channelNumber)
  const name = locked ? '' : (channelName ?? '').trim()

  if (number && name) return `${number}. ${name}`
  return number || name
}

/**
 * Elapsed percentage of a programme, clamped to 0…100 (UI-5.6). Returns `null`
 * when the EPG timestamps are missing or inconsistent (AC-3), so no progress
 * bar is drawn.
 */
export const getProgrammeProgress = ({
  startMs,
  stopMs,
  nowMs,
}: {
  startMs?: number
  stopMs?: number
  nowMs: number
}): number | null => {
  if (startMs == null || stopMs == null) return null
  if (!Number.isFinite(startMs) || !Number.isFinite(stopMs)) return null
  if (stopMs <= startMs) return null

  const progress = ((nowMs - startMs) / (stopMs - startMs)) * 100
  return Math.min(100, Math.max(0, progress))
}
